import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle, ChevronRight, AlertCircle, Info, Lock } from 'lucide-react';
import { completeEarningAction } from '@/api/rewards';

interface ZKPSetupFlowProps {
  verificationType: 'vaccination' | 'age-eligibility' | 'weight-eligibility' | 'allergy-presence' | 'medication-status' | 'license-valid';
  context: 'booking' | 'emergency' | 'professional-verification' | 'sharing';
  onComplete: (proof: any) => void;
  onCancel: () => void;
}

type FlowStep = 'education' | 'choose-type' | 'consent' | 'generating' | 'success';

const CONTEXT_KEYS: Record<string, string> = {
  booking: 'contextBooking',
  emergency: 'contextEmergency',
  'professional-verification': 'contextProfessional',
  sharing: 'contextSharing',
};

export function ZKPSetupFlow({ verificationType, context, onComplete, onCancel }: ZKPSetupFlowProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<FlowStep>('education');
  const [selectedType, setSelectedType] = useState(verificationType);
  const [usePrivacyProtected, setUsePrivacyProtected] = useState(true);
  const [consented, setConsented] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);

  const contextLabel = t(`landing.features.zkp.setupFlow.${CONTEXT_KEYS[context]}`);
  const forContextLabel = t('landing.features.zkp.setupFlow.forContext', { context: contextLabel });

  useEffect(() => {
    const rafId = requestAnimationFrame(() => setIsAnimated(true));
    return () => cancelAnimationFrame(rafId);
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const verificationTypeKeys = ['vaccination', 'age-eligibility', 'weight-eligibility', 'allergy-presence', 'medication-status', 'license-valid'] as const;
  const verificationTypes = Object.fromEntries(
    verificationTypeKeys.map((key) => [
      key,
      {
        name: t(`landing.features.zkp.proofTypes.${key}`),
        description: t(`landing.features.zkp.proofTypeDesc.${key}`),
        icon: key === 'vaccination' ? '💉' : key === 'age-eligibility' ? '🎂' : key === 'weight-eligibility' ? '⚖️' : key === 'allergy-presence' ? '🚨' : key === 'medication-status' ? '💊' : '🏥',
        example: t(`landing.features.zkp.proofTypeExample.${key}`),
      },
    ])
  ) as Record<string, { name: string; description: string; icon: string; example: string }>;

  const handleGenerate = () => {
    setCurrentStep('generating');

    // Simulate proof generation
    setTimeout(() => {
      setCurrentStep('success');

      // Auto-reward ZKP Usage (r10) — fire-and-forget, don't block the flow
      completeEarningAction('r10').catch(() => {
        // Silently ignore — may already be completed or user not authed
      });

      setTimeout(() => {
        onComplete({
          type: selectedType,
          verified: true,
          created: new Date().toISOString(),
          expires: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString(),
          context
        });
      }, 1500);
    }, 2000);
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      {/* Darkened overlay: page content visible but dimmed + subtle blur (like Microchip modal) */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
        aria-hidden="true"
      />
      <div
        className={`relative z-10 bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-3xl border border-gray-200/80 transition-all duration-300 ease-out ${
          isAnimated ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          boxShadow:
            '0 0 0 1px rgba(0,0,0,0.04), 0 2px 4px rgba(0,0,0,0.04), 0 12px 24px -4px rgba(0,0,0,0.08), 0 24px 48px -8px rgba(0,0,0,0.12), 0 0 0 1px rgba(99,102,241,0.08), 0 32px 64px -12px rgba(99,102,241,0.12)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                {t('landing.features.zkp.setupFlow.title')}
              </h2>
              <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                {forContextLabel}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Education */}
          {currentStep === 'education' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl mb-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                  {t('landing.features.zkp.setupFlow.verifyWithoutSharingHeading')}
                </h3>
                <p style={{ color: '#464452', fontFamily: 'Inter, sans-serif', lineHeight: '1.6' }}>
                  {t('landing.features.zkp.setupFlow.verifyWithoutSharingIntro')}
                </p>
              </div>

              {/* How It Works */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold mb-4 flex items-center gap-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                  <Info className="w-5 h-5 text-blue-600" />
                  {t('landing.features.zkp.setupFlow.howItWorks')}
                </h4>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      1
                    </div>
                    <div>
                      <div className="font-semibold mb-1" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.step1Title')}
                      </div>
                      <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.step1Desc')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      2
                    </div>
                    <div>
                      <div className="font-semibold mb-1" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.step2Title')}
                      </div>
                      <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.step2Desc')}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      3
                    </div>
                    <div>
                      <div className="font-semibold mb-1" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.step3Title')}
                      </div>
                      <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.step3Desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Example */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{verificationTypes[selectedType].icon}</div>
                  <div>
                    <h4 className="font-semibold mb-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                      {t('landing.features.zkp.setupFlow.exampleLabel')} {verificationTypes[selectedType].name}
                    </h4>
                    <p className="text-sm mb-2" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                      {verificationTypes[selectedType].description}
                    </p>
                    <div className="bg-white rounded-lg p-3 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <div className="flex items-center gap-2 text-green-600 mb-1">
                        <CheckCircle className="w-4 h-4" />
                        <strong>{t('landing.features.zkp.setupFlow.whatProfessionalSees')}</strong>
                      </div>
                      <div style={{ color: '#464452' }}>{verificationTypes[selectedType].example}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                    <strong style={{ color: '#2D2F7E' }}>{t('landing.features.zkp.setupFlow.importantLabel')}</strong>
                    <ul className="mt-2 space-y-1">
                      <li>• {t('landing.features.zkp.setupFlow.noteExpire')}</li>
                      <li>• {t('landing.features.zkp.setupFlow.noteFullRecords')}</li>
                      <li>• {t('landing.features.zkp.setupFlow.noteEmergency')}</li>
                      <li>• {t('landing.features.zkp.setupFlow.noteRevoke')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={onCancel}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer"
                  style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('landing.features.zkp.setupFlow.cancel')}
                </button>
                <button
                  onClick={() => setCurrentStep('choose-type')}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('landing.features.zkp.setupFlow.continue')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Choose Type */}
          {currentStep === 'choose-type' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl mb-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                  {t('landing.features.zkp.setupFlow.chooseVerificationType')}
                </h3>
                <p style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  {t('landing.features.zkp.setupFlow.selectWhatToVerify', { context: contextLabel })}
                </p>
              </div>

              {/* Verification Options */}
              <div className="space-y-3">
                {Object.entries(verificationTypes).map(([key, type]) => (
                  <label
                    key={key}
                    className={`block p-5 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedType === key
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <input
                        type="radio"
                        name="verification-type"
                        value={key}
                        checked={selectedType === key}
                        onChange={(e) => setSelectedType(e.target.value as any)}
                        className="mt-1 w-5 h-5 text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">{type.icon}</span>
                          <h4 className="font-semibold" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                            {type.name}
                          </h4>
                        </div>
                        <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                          {type.description}
                        </p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Privacy Toggle */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.privacyProtectedRecommended')}
                      </h4>
                      <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                        {t('landing.features.zkp.setupFlow.shareBadgeOnly')}
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={usePrivacyProtected}
                      onChange={(e) => setUsePrivacyProtected(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-blue-600 peer-checked:to-purple-600"></div>
                  </label>
                </div>
                {!usePrivacyProtected && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                    <AlertCircle className="w-4 h-4 text-amber-600 inline mr-2" />
                    {t('landing.features.zkp.setupFlow.fullRecordsWarning')}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentStep('education')}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer"
                  style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('landing.features.zkp.setupFlow.back')}
                </button>
                <button
                  onClick={() => setCurrentStep('consent')}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('landing.features.zkp.setupFlow.continue')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Consent */}
          {currentStep === 'consent' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl mb-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                  {t('landing.features.zkp.setupFlow.confirmVerification')}
                </h3>
                <p style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  {t('landing.features.zkp.setupFlow.reviewAndConfirm')}
                </p>
              </div>

              {/* Summary */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="text-3xl">{verificationTypes[selectedType].icon}</div>
                  <div>
                    <h4 className="font-semibold" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                      {verificationTypes[selectedType].name}
                    </h4>
                    <p className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                      {forContextLabel}
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 space-y-2 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span style={{ color: '#464452' }}>{t('landing.features.zkp.setupFlow.verificationWillBeProtected')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span style={{ color: '#464452' }}>{t('landing.features.zkp.setupFlow.validFor6Months')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span style={{ color: '#464452' }}>{t('landing.features.zkp.setupFlow.canBeRevokedAnytime')}</span>
                  </div>
                </div>
              </div>

              {/* Consent Checkbox */}
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                <input
                  type="checkbox"
                  id="zkp-consent"
                  checked={consented}
                  onChange={(e) => setConsented(e.target.checked)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="zkp-consent" className="flex-1 cursor-pointer text-sm" style={{ color: '#2D2F7E', fontFamily: 'Inter, sans-serif' }}>
                  {t('landing.features.zkp.setupFlow.consentLabel')}
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCurrentStep('choose-type')}
                  className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 hover:bg-gray-50 transition-colors cursor-pointer"
                  style={{ color: '#464452', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('landing.features.zkp.setupFlow.back')}
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={!consented}
                  className="flex-1 px-6 py-3 rounded-xl text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  <Shield className="w-5 h-5" />
                  {t('landing.features.zkp.setupFlow.verifyNow')}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Generating */}
          {currentStep === 'generating' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl mb-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                {t('landing.features.zkp.setupFlow.creatingVerification')}
              </h3>
              <p className="mb-6" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                {t('landing.features.zkp.setupFlow.encryptingAndGenerating')}
              </p>
              <div className="max-w-md mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-[progress_2s_ease-in-out]"
                    style={{ width: '0%', animation: 'progress 2s ease-in-out forwards' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Success */}
          {currentStep === 'success' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-2xl mb-2" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>
                {t('landing.features.zkp.setupFlow.verificationCreated')}
              </h3>
              <p className="mb-6" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                {t('landing.features.zkp.setupFlow.verificationActive')}
              </p>
              <div className="bg-gradient-to-br from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6 max-w-md mx-auto">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-2xl">{verificationTypes[selectedType].icon}</span>
                  <span className="font-semibold" style={{ color: '#2D2F7E', fontFamily: 'Sora, sans-serif' }}>
                    {verificationTypes[selectedType].name}
                  </span>
                </div>
                <div className="text-sm" style={{ color: '#464452', fontFamily: 'Inter, sans-serif' }}>
                  {t('landing.features.zkp.setupFlow.validUntil')} {new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        @keyframes progress {
          from {
            width: 0%;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
}
